import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";
import { CommonImages } from "../../../../reusableUI/images";
import {
  addAdminUser,
  getAdminUsersList,
  updateAdminStatus,
  deleteAdminUser,
} from "../../../../Service/RM";
import { getLookupData } from "../../../../Service/lookup";
import Loader from "../../../../reusableUI/loader";
import { showToast } from "../../../../reusableUI/toaster";
import { FiTrash2 } from "react-icons/fi";
import Switch from "react-switch";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import { User } from "lucide-react";
import Item from "antd/es/list/Item";

const ManageAccess = ({ searchTerm }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [paginationPage, setPaginationPage] = useState(0);
  const [title, setTitle] = useState("");
  const [fullName, setFullName] = useState("");
  const [casedata, setCase] = useState([]);
  const [toastMsg, setToastMsg] = useState(null);
  const [checked, setChecked] = useState();
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    getCase();
    getList();
  }, []);

  const getCase = async () => {
    const result = await getLookupData();
    if (result.error === false) {
      setCase(result.data);
    } else {
      console.log("Error fetching");
    }
  };

  const getList = async () => {
    const result = await getAdminUsersList();
    if (result.error === false) {
      const activeUsers = result.data.filter(
        (item) => item.status !== "deleted"
      );
      setUserList(activeUsers);
    } else {
      console.log("Error fetching");
    }
  };

  const caseTitle = casedata.filter((item) => item.type === "title");

  const handleInvite = async () => {
    if (!title) {
      showToast("Please enter a title.", "Error");
      return;
    }
    if (!fullName) {
      showToast("Please enter the full name.", "Error");
      return;
    }
    if (!email) {
      showToast("Please enter an email.", "Error");
      return;
    }
    if (!role) {
      showToast("Please select a role.", "Error");
      return;
    }

    const data = {
      nameInfo: {
        title: title,
        fullName: fullName,
      },
      role: role,
      email: email,
    };

    console.log("Sending Data:", data);

    try {
      setLoading(true);
      const req = await addAdminUser(data);

      if (req.error === false) {
        console.log("User added:", req.data);
        showToast("User added successfully.", "Success");

        setTitle("");
        setFullName("");
        setEmail("");
        setRole("");
        getList();
      } else {
        showToast("Failed to add user.", "Error");
      }
    } catch (err) {
      console.error("Invite Error:", err);
      showToast("Something went wrong.", "Error");
    } finally {
      setLoading(false);
    }
  };

  // console.log(userList);

  const handleDelete = async (user) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this user?"
    );
    if (!confirmDelete) {
      return;
    }

    try {
      const req = await deleteAdminUser(user);
      console.log(req);
      if (req.error === false) {
        showToast("User Deleted", "Success");
        getList();
      } else {
        showToast("Something went wrong.", "Error");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      showToast("Something went wrong.", "Error");
    }
  };

  const handleToggleStatus = async (user) => {
    const confirmUpdate = window.confirm(
      "Are you sure you want to update status?"
    );
    if (!confirmUpdate) return;

    const newStatus = user.status === "active" ? "inactive" : "active";
    console.log("Toggle status for", user, "->", newStatus);

    const data = {
      status: newStatus,
    };

    try {
      const req = await updateAdminStatus(user, data);
      console.log(req);
      if (req.error === false) {
        console.log(req.data);
        showToast("Status changed", "Success");
        getList();
      } else {
        console.log(req.error);
        showToast("Failed to update status.", "Error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Something went wrong.", "Error");
    }
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Validate email with regex
    if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
  };

  const getColumns = () => {
    let baseColumns = [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ row: { original } }) => {
          return (
            <p className="ml-3 text-[#504EFF] font-medium text-[12px]">
              {original?.nameInfo?.title + ". " + original?.nameInfo?.fullName}
            </p>
          );
        },
      },
      {
        Header: "Email Address",
        accessor: "email",
        Cell: ({ row: { original } }) => {
          return <p className="ml-3">{original?.email}</p>;
        },
      },

      {
        Header: "Role",
        accessor: "rol",
        Cell: ({ row: { original } }) => (
          <p className="text-gray-500 text-[12px] ml-3">
            {original.role === "relationship-manager"
              ? "Relationship Manager"
              : "Support"}
          </p>
        ),
      },
      {
        Header: "Status",
        accessor: "location",
        Cell: ({ row: { original } }) => (
          <div>
            <p className="text-gray-500 text-[12px] ml-4">
              {capitalizeFirstLetter(original?.status) || "not available"}
            </p>
          </div>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row: { original } }) => (
          <div className="flex items-center gap-6 ml-2">
            <Switch
              checked={original?.status === "active"}
              onChange={() => handleToggleStatus(original)}
              onColor="#4951EC"
              offColor="#d1d5db"
              uncheckedIcon={false}
              checkedIcon={false}
              height={10}
              width={20}
            />
            <FiTrash2
              className="text-red-500 cursor-pointer"
              size={14}
              onClick={() => handleDelete(original)}
            />
          </div>
        ),
      },
    ];

    return baseColumns;
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { pageIndex, sortBy: currentSortBy },

    gotoPage,
    page,
  } = useTable(
    {
      columns: useMemo(() => getColumns(), []),
      data: userList,
      initialState: {
        pageIndex: paginationPage,
      },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    localStorage.setItem("sortBy", JSON.stringify(currentSortBy));
  }, [currentSortBy]);

  useEffect(() => {
    if (pageIndex !== paginationPage) {
      setPaginationPage(pageIndex);
    }
  }, [pageIndex]);

  //   useEffect(() => {
  //     if (location.state?.currentPage !== undefined) {
  //       setPaginationPage(location.state.currentPage);
  //     }
  //   }, [location.state]);

  return (
    <div>
      {loading ? (
        <div className="">
          <Loader loading={loading} />
        </div>
      ) : (
        <div className="bg-[#F3F6F5] ">
          <div className="bg-white rounded-xl shadow w-[98%] m-2 p-4">
            <h2 className="text-[14px] font-semibold mb-2">
              Add Support Roles
            </h2>

            <div className="flex flex-wrap gap-4 items-start justify-between border-t border-gray-200 p-4">
              {/* Title + Full Name */}
              <div className="flex flex-col min-w-[280px]">
                <div className="flex items-center gap-4 mb-1">
                  <label className="text-[12px] font-medium text-gray-700 ">
                    Title
                  </label>
                  <label className="text-[12px] font-medium text-gray-700  ml-10">
                    Full Name{" "}
                    <span className="text-red-600  text-[18px]">*</span>
                  </label>
                </div>
                <div className="flex">
                  <div className="relative w-[70px]">
                    <select
                      className="appearance-none w-full border border-[#D2D2D2] px-2 py-1.5 rounded-sm text-[12px] text-[#606060] font-poppins"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    >
                      <option hidden></option>
                      {caseTitle.map((item, index) => (
                        <option
                          key={`${item.value}-${index}`}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center justify-center mr-2">
                      <svg
                        className="w-4 h-4 text-[#606060]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.25 4.66a.75.75 0 01-1.08 0l-4.25-4.66a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  <input
                    type="text"
                    className="border border-[#D2D2D2] w-[240px] px-2 py-1 rounded-r-sm text-[12px]"
                    placeholder=""
                    value={fullName}
                    onChange={(e) => {
                      const val = e.target.value;
                      const capitalized =
                        val.charAt(0).toUpperCase() + val.slice(1);
                      setFullName(capitalized);
                    }}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-col min-w-[250px]">
                <label className="text-[12px] font-medium text-gray-700 mb-1">
                  Email Address
                  <span className="text-red-600 text-[18px]">*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter"
                  value={email}
                  onChange={handleEmailChange}
                  className={`border rounded-sm px-3 py-1.5 text-[12px] focus:outline-none focus:ring-2 ${
                    emailError
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {/* Reserve fixed space for error */}
                <span
                  className={`text-[10px] mt-1 ${
                    emailError ? "text-red-500" : "invisible"
                  }`}
                >
                  {emailError || "placeholder"}
                </span>
              </div>

              {/* Role */}
              <div className="flex flex-col min-w-[200px]">
                <label className="text-[12px] font-medium text-gray-700 mb-1">
                  Role
                  <span className="text-red-600  text-[18px]">*</span>
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="border border-gray-300 rounded-sm px-3 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" hidden>
                    Select
                  </option>

                  <option value="support">Support</option>
                  <option value="relationship-manager">
                    Relationship Manager
                  </option>
                </select>
              </div>

              {/* Invite Button */}
              <div className="flex items-end">
                <button
                  onClick={handleInvite}
                  className="bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-md px-6 py-1 mt-8 text-sm hover:opacity-90 transition"
                >
                  Invite
                </button>
              </div>
            </div>
          </div>
          <div className="w-[98%] bg-white rounded-2xl overflow-hidden h-[63vh] shadow-lg mt-2 m-2">
            <p className="p-3 text-[14px] ml-1 font-semibold ">
              Support & Relationship Managers ({userList.length})
            </p>
            <div className="overflow-x-auto max-h-[52vh]">
              <div className="min-w-full w-[100%]">
                <ReusableTable
                  columns={getColumns()}
                  getTableProps={getTableProps}
                  getTableBodyProps={getTableBodyProps}
                  headerGroups={headerGroups}
                  tablePage={tablePage}
                  prepareRow={prepareRow}
                  filterIcon={CommonImages.filter}
                  searchTerm={searchTerm}
                />
              </div>
            </div>
            <div className="mb-2">
              <Page
                paginationPage={paginationPage}
                nextPage={nextPage}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                // onPageChange={handleChange2}
              />
            </div>
          </div>
          {/* {toastMsg && <Toaster action={toastMsg.action} msg={toastMsg.msg} />} */}
        </div>
      )}
    </div>
  );
};

export default ManageAccess;
